import {
  Button,
  ButtonGroup,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  closeCurrentDialog,
} from 'platform/components';
import {Grid, GridItem, VStack} from 'platform/foundation';
import {boolean, object} from 'yup';

import {isTrue} from 'ramda-adjunct';

import {GetTenantV2ApiRes} from '@dms/api/base';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {usePhoneNumbers} from '@dms/shared';

import {yupString} from 'shared/web';

type FormValues = GetTenantV2ApiRes['contactPersons'][number];

interface EditContactPersonFormProps {
  onSubmit: (item: FormValues) => void;
  defaultValues?: FormValues;
}

export function ContactPersonForm(props: EditContactPersonFormProps) {
  const {countriesOptions} = usePhoneNumbers();

  const handleSubmit: FormSubmitHandler<FormValues> = async (values) => {
    await props.onSubmit(values);
    closeCurrentDialog();
  };

  return (
    <Form<FormValues> onSubmit={handleSubmit} defaultValues={props.defaultValues} schema={schema}>
      {(control) => (
        <VStack spacing={5}>
          <Grid columns={2}>
            <GridItem span={2}>
              <FormField
                control={control}
                name="isPrimary"
                isDisabled={isTrue(props.defaultValues?.isPrimary)}
                type="switch"
                label={i18n.t('entity.customer.labels.contactPersonsHelperText')}
              />
            </GridItem>

            <FormField
              control={control}
              name="firstName"
              type="text"
              label={i18n.t('general.labels.firstName')}
              data-testid={testIds.settings.branchesEdit('firstName')}
            />
            <FormField
              control={control}
              name="lastName"
              type="text"
              label={i18n.t('general.labels.lastName')}
              data-testid={testIds.settings.branchesEdit('lastName')}
            />

            <FormField
              control={control}
              name="role"
              type="text"
              label={i18n.t('entity.person.labels.role')}
              data-testid={testIds.settings.branchesEdit('role')}
            />
          </Grid>
          <Grid columns={2}>
            <FormField
              control={control}
              name="phoneNumber"
              type="phone"
              countries={countriesOptions}
              label={i18n.t('entity.phoneNumber.labels.number')}
              data-testid={testIds.settings.branchesEdit('phoneNumber')}
            />
            <FormField
              control={control}
              name="emailAddress"
              type="text"
              label={i18n.t('general.labels.email')}
              data-testid={testIds.settings.branchesEdit('email')}
            />
          </Grid>

          <ButtonGroup align="right">
            <Button
              title={i18n.t('general.actions.discard')}
              onClick={closeCurrentDialog}
              variant="secondary"
              data-testid={testIds.settings.branchesEdit('discard')}
            />
            <FormButton
              control={control}
              type="submit"
              data-testid={testIds.settings.branchesEdit('saveContactPerson')}
              title={i18n.t('general.actions.save')}
            />
          </ButtonGroup>
        </VStack>
      )}
    </Form>
  );
}

const schema = object({
  isPrimary: boolean().default(false).required(),
  firstName: yupString.required(),
  lastName: yupString.required(),
  role: yupString.default(null),
  phoneNumber: object().default(null).nullable(),
  emailAddress: yupString.default(null),
});
